import * as React from 'react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Command, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import styled from 'styled-components';

import { MenuItems } from 'types';
import { ExitButton, UserInfo } from 'atoms';
import { MenuItemList, LanguageSwitcher, ThemeSwitcher } from 'molecules';

import { HEADER_COMPACT_MEDIA, HEADER_MID_MEDIA } from 'components/constants';
import { AI_ACCENT } from 'components/constants/uiColors';

export interface IMenuComponent {
  activeMenu?: MenuItems;
  menuItems: ReactNode[];
  onExit: () => void;
  /** Opens the command palette — a pointer entry point for touch / no-keyboard. */
  onOpenPalette: () => void;
  /**
   * Колокольчик уведомлений (ТЗ 0068) — СЛОТОМ, а не встроенным контейнером.
   *
   * Этот компонент презентационный и имеет сториз; контейнер, тянущий данные
   * через Apollo, сломал бы их. Тот же приём уже применён к `menuItems`.
   */
  notifications?: ReactNode;
  userFirstName?: string | null;
  userLastName?: string | null;
  userEmail?: string | null;
  /** ТЗ 0083: клик по имени — «Мой профиль» (рабочая почта, правила). */
  onOpenProfile?: () => void;
  /**
   * ТЗ 0106: адрес ИИ-чата — кнопка-иконка рядом с палитрой. Нет адреса
   * (роль без чата) — нет и кнопки.
   */
  aiChatTo?: string | null;
  /** Открыт сам чат — кнопка подсвечена. */
  aiChatActive?: boolean;
}

export const MenuComponent = ({
  menuItems,
  onExit,
  onOpenPalette,
  notifications,
  userFirstName,
  userLastName,
  userEmail,
  onOpenProfile,
  aiChatTo,
  aiChatActive,
}: IMenuComponent) => {
  const { t } = useTranslation();
  const fullName = [userFirstName, userLastName].filter(Boolean).join(' ');
  // Пока пользователь не загрузился, кнопка была бы пустой рамкой.
  const who = fullName || userEmail || '';
  const profileLabel = t('profile.openFor', { name: who });

  return (
    <Wrapper>
      <Container>
        <MenuItemList>{menuItems}</MenuItemList>
        <RightGroup>
          {notifications}
          {aiChatTo ? (
            <AiChatTrigger
              to={aiChatTo}
              $active={Boolean(aiChatActive)}
              title={t('aiChat.headerButton')}
              aria-label={t('aiChat.headerButton')}
              aria-current={aiChatActive ? 'page' : undefined}
            >
              <Sparkles size={19} strokeWidth={2} aria-hidden="true" />
            </AiChatTrigger>
          ) : null}
          <PaletteTrigger
            type="button"
            onClick={onOpenPalette}
            title={t('commandPalette.help.openTrigger')}
            aria-label={t('commandPalette.help.openTrigger')}
          >
            <Command size={20} strokeWidth={2} />
          </PaletteTrigger>
          <ThemeSwitcher />
          <LanguageSwitcher />
          {who && (
            <ProfileTrigger
              type="button"
              onClick={onOpenProfile}
              disabled={!onOpenProfile}
              title={
                fullName && userEmail
                  ? `${profileLabel}\n${userEmail}`
                  : profileLabel
              }
              aria-label={profileLabel}
            >
              <UserInfo
                firstName={userFirstName}
                lastName={userLastName}
                email={userEmail}
              />
            </ProfileTrigger>
          )}
          <ExitButton onClick={onExit} />
        </RightGroup>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: var(--k-bg-ffffff);
  padding: 21px 2px;
`;

/**
 * `min-width: 0` — иначе flex-ряд не ужимается уже своего содержимого и
 * выталкивает страницу в горизонтальную прокрутку. Меню (`MenuItemList`)
 * тянется и ужимается, правая группа — нет.
 */
const Container = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  box-sizing: inherit;

  ${HEADER_COMPACT_MEDIA} {
    gap: 8px;
  }
`;

/**
 * Зазор 12, а не 16: шапке нужно место под шесть групп меню на 1280 px; уже
 * брейкпоинта — 8. Не ужимается: палитра, язык, профиль и выход нужны всегда
 * (палитра — единственный вход в команды на сенсорном экране).
 */
const RightGroup = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 12px;

  ${HEADER_MID_MEDIA} {
    gap: 8px;
  }
`;

const PaletteTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid var(--k-border-dfe6f1);
  border-radius: 8px;
  cursor: pointer;
  color: var(--k-fg-616e78);
  background: var(--k-bg-ffffff);
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--k-fg-0d53fc);
    border-color: var(--k-border-0d53fc);
  }
`;

/**
 * ТЗ 0106: ИИ-чат — тот же квадрат, что у палитры, но с градиентом бренда
 * чата при наведении и на самой странице (цвета — палитра доски задач:
 * синий → фиолетовый).
 */
const AiChatTrigger = styled(Link)<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid
    ${({ $active }) => ($active ? 'transparent' : 'var(--k-border-dfe6f1)')};
  border-radius: 8px;
  color: ${({ $active }) =>
    $active ? 'var(--k-fg-ffffff)' : AI_ACCENT.violet};
  background: ${({ $active }) =>
    $active ? AI_ACCENT.gradient : 'var(--k-bg-ffffff)'};
  transition: color 0.15s ease, border-color 0.15s ease;

  &:hover {
    color: var(--k-fg-ffffff);
    border-color: transparent;
    background: ${AI_ACCENT.gradient};
  }

  &:focus-visible {
    outline: 2px solid var(--k-border-0d53fc);
    outline-offset: 2px;
  }
`;

const ProfileTrigger = styled.button`
  padding: 4px 6px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  font: inherit;
  text-align: right;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: var(--k-border-dfe6f1);
  }

  &:focus-visible {
    outline: none;
    border-color: var(--k-border-0d53fc);
  }

  &:disabled {
    cursor: default;
  }
`;
